import React, { createContext, useState, useCallback } from 'react';
import { api, API_ENDPOINTS } from '../config/api';
import { LOADING_STATES, ERROR_MESSAGES } from '../utils/constants';

export const DashboardContext = createContext();

export const DashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingState, setLoadingState] = useState(LOADING_STATES.IDLE);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  /**
   * Obtiene los datos del dashboard
   * @param {number} farmaciaId - ID de la farmacia
   * @param {boolean} forceRefresh - Forzar actualización ignorando cache
   */
  const fetchDashboard = useCallback(async (farmaciaId, forceRefresh = false) => {
    if (!farmaciaId) {
      setError('ID de farmacia no proporcionado');
      return;
    }

    setLoadingState(LOADING_STATES.LOADING);
    setError(null);

    try {
      const params = { farmacia: farmaciaId };
      if (forceRefresh) {
        params._t = Date.now(); // Cache busting solo si es refresh forzado
      }
      
      const data = await api.get(API_ENDPOINTS.dashboard, params);

      setDashboardData(data);
      setLastUpdate(new Date());
      setLoadingState(LOADING_STATES.SUCCESS);
      
      return { success: true, data };
    } catch (err) {
      const errorMessage = err.message || ERROR_MESSAGES.serverError;
      setError(errorMessage);
      setLoadingState(LOADING_STATES.ERROR);
      
      return { success: false, error: errorMessage };
    }
  }, []);

  /**
   * Refresca los datos del dashboard
   * @param {number} farmaciaId - ID de la farmacia
   */
  const refreshDashboard = useCallback((farmaciaId) => {
    return fetchDashboard(farmaciaId, true);
  }, [fetchDashboard]);

  /**
   * Limpia los datos del dashboard
   */
  const clearDashboard = useCallback(() => {
    setDashboardData(null);
    setLoadingState(LOADING_STATES.IDLE);
    setError(null);
    setLastUpdate(null);
  }, []);

  /**
   * Verifica si los datos están cargando
   */
  const isLoading = loadingState === LOADING_STATES.LOADING;

  /**
   * Verifica si hay un error
   */
  const hasError = loadingState === LOADING_STATES.ERROR;

  /**
   * Verifica si los datos están cargados
   */
  const isLoaded = loadingState === LOADING_STATES.SUCCESS && dashboardData !== null;

  const value = {
    // Estado
    dashboardData,
    loadingState,
    error,
    lastUpdate,
    
    // Acciones
    fetchDashboard,
    refreshDashboard,
    clearDashboard,
    
    // Helpers
    isLoading,
    hasError,
    isLoaded,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext;

// Made with Bob
